import React from "react"

import { TruncatedText } from "@coval/ui"
import { ChevronDown, ChevronRight, Wrench } from "lucide-react"

import { extractToolCallName } from "./toolCallName"
import { useShowToolCallNames } from "./ToolCallNameDisplayContext"

interface ToolCallMessageProps {
  content: unknown
  className?: string
}

interface ParsedToolCallContent {
  function: string
  arguments: unknown
}

const isToolCallContent = (content: unknown): boolean => {
  if (typeof content === "object" && content !== null) {
    return (
      "function" in content ||
      "tool_call" in content ||
      "tool" in content ||
      "tool_name" in content ||
      "dtmf_tool" in content
    )
  }

  if (typeof content === "string") {
    try {
      const parsed: unknown = JSON.parse(content)
      return (
        typeof parsed === "object" &&
        parsed !== null &&
        ("tool_call" in parsed ||
          "function" in parsed ||
          "tool" in parsed ||
          "tool_name" in parsed ||
          "dtmf_tool" in parsed)
      )
    } catch {
      return (
        content.trim().startsWith("{tool_call:") &&
        content.includes("arguments:")
      )
    }
  }

  return false
}

const parseToolCallContent = (content: unknown): ParsedToolCallContent => {
  // If content is already an object, use it directly
  if (typeof content === "object" && content !== null) {
    // Format: { tool_name: "...", content: {...} }
    if ((content as { tool_name?: unknown }).tool_name) {
      return {
        function: (content as { tool_name?: string }).tool_name as string,
        arguments: (content as { content?: unknown }).content || content,
      }
    }

    // Check for standard OpenAI tool call format
    if (
      (content as { function?: unknown }).function &&
      typeof (content as { function?: unknown }).function === "object"
    ) {
      const toolFunction = (content as { function: { name?: unknown } })
        .function
      const functionName =
        typeof toolFunction.name === "string" && toolFunction.name.trim() !== ""
          ? toolFunction.name
          : "Unknown Tool"
      let args =
        (content as { function: { arguments?: unknown } }).function.arguments ||
        {}

      // If arguments is a string, try to parse it
      if (typeof args === "string") {
        try {
          args = JSON.parse(args) as Record<string, unknown>
        } catch {
          // If parsing fails, keep it as string
        }
      }

      return {
        function: functionName,
        arguments: args,
      }
    }

    // If it has a direct function name and arguments (or data)
    if (
      (content as { function?: unknown }).function &&
      ((content as { arguments?: unknown }).arguments !== undefined ||
        (content as { data?: unknown }).data !== undefined)
    ) {
      return {
        function: (content as { function?: string }).function as string,
        arguments:
          (content as { arguments?: unknown }).arguments ||
          (content as { data?: unknown }).data ||
          content,
      }
    }

    // Legacy format with tool_call field
    if ((content as { tool_call?: unknown }).tool_call) {
      return {
        function: (content as { tool_call?: string }).tool_call as string,
        arguments: (content as { arguments?: unknown }).arguments || content,
      }
    }

    // Otherwise, just display the whole object as the content
    return {
      function: "Tool Call",
      arguments: content,
    }
  }

  // If content is a string, try to parse it
  if (typeof content === "string") {
    try {
      const parsed: unknown = JSON.parse(content)
      // Recursively parse the parsed object
      return parseToolCallContent(parsed)
    } catch {
      // If it's not valid JSON, return it as-is
      return {
        function: "Tool Call",
        arguments: content,
      }
    }
  }

  // Fallback for any other type
  return {
    function: "Tool Call",
    arguments: content,
  }
}

export const ToolCallMessage: React.FC<ToolCallMessageProps> = ({
  content,
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const showToolCallNames = useShowToolCallNames()

  if (!isToolCallContent(content)) {
    return null
  }

  const fullToolCallStructure = parseToolCallContent(content)

  // When the org has the feature flag enabled, surface the tool name inline (no
  // expand needed), recovering it from shapes `parseToolCallContent` would
  // otherwise label as a generic "Tool Call". When off, keep the existing label.
  const robustToolCallName = showToolCallNames
    ? extractToolCallName(content)
    : null
  const displayName = robustToolCallName ?? fullToolCallStructure.function

  const fullToolCallString = JSON.stringify(fullToolCallStructure, null, 2)

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div
      className={`my-2 rounded-lg bg-inherit border border-coval-border p-3 ${className}`}
    >
      <button
        className="flex items-center justify-between gap-2 w-full min-w-0 hover:opacity-80 transition-opacity"
        onClick={handleToggleExpanded}
        type="button"
        aria-label={`Toggle tool call details for ${displayName}`}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Wrench className="w-3 h-3 shrink-0 text-coval-text" />
          <TruncatedText className="font-medium text-xs text-coval-text">
            {displayName}
          </TruncatedText>
        </div>
        <div className="flex items-center gap-2 shrink-0 opacity-60">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-coval-border">
          <pre className="font-mono leading-normal whitespace-pre-wrap wrap-break-word text-xs text-coval-text">
            {fullToolCallString}
          </pre>
        </div>
      )}
    </div>
  )
}
